import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { InsightsGroupByItem } from '@shared/api/types';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import {
  RATING_ORDER,
  ratingsChartConfig,
} from '@shared/components/chart/configs';
import SimpleAreaChart from '@shared/components/chart/simple-area-chart';
import SimpleScatterChart, {
  ScatterPoint,
} from '@shared/components/chart/simple-scatter-chart';
import { DashboardSection } from '@shared/components/dashboard/dashboard-section';
import SimpleSelect from '@shared/components/simple-select';
import { Card } from '@shared/components/ui/card';
import { ChartConfig } from '@shared/components/ui/chart';
import {
  formatCompact,
  formatTonnes,
  getCurrencySymbol,
} from '@shared/lib/format';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import Text from '@shared/components/content/text';
import { Icons } from '@shared/components/content/icons';
import {
  InsightsChartType,
  useInsightsChartType,
} from '../hooks/use-insights-filters';
import { ChartAxisTitles } from './chart-axis-titles';
import { isCompletedMonth } from '../lib/dense-series';
import { InsightsNoSpendData } from './insights-no-spend-data';

const SERIES_COLORS = [
  'var(--chart-org-1)',
  'var(--chart-org-2)',
  'var(--chart-org-3)',
  'var(--chart-org-4)',
  'var(--chart-org-5)',
  'var(--chart-org-6)',
];
const OTHERS_COLOR = 'var(--chart-org-other)';
const MAX_NAMED_SERIES = SERIES_COLORS.length;
const DEFAULT_POINT_COLOR = 'var(--chart-food-and-drink)';

// Ordered for the grouped-view selector: scatter comparisons first (scatter is
// the grouped default), then the over-time series.
const CHART_LABELS: Record<InsightsChartType, string> = {
  scatter: 'Emissions vs spend',
  intensity: 'Intensity comparison',
  emissions: 'Emissions over time',
  spend: 'Spend over time',
};

/** Chart types that plot spend figures, gated behind the cost feature. */
export const SPEND_CHART_TYPES: readonly InsightsChartType[] = [
  'scatter',
  'intensity',
  'spend',
];

/** Rating colour for a row, falling back to the default series colour. */
/**
 * What identifies a breakdown row across the table and the charts, so a hover
 * on one lands on the same row in the other. Site, supplier and item rows carry
 * a uuid; the taxonomy tiers a slug; the name is the last resort.
 */
export const insightsRowId = (row: InsightsGroupByItem) =>
  row.uuid ?? row.slug ?? row.name;

function ratingColor(rating: string | null): string {
  const key = rating?.toUpperCase() as (typeof RATING_ORDER)[number];
  return RATING_ORDER.includes(key)
    ? ratingsChartConfig[key].color
    : DEFAULT_POINT_COLOR;
}

/** One point of a row's activityOverTime series, as the series builder reads it. */
type ActivitySeriesPoint = InsightsGroupByItem['activityOverTime'][number];

/**
 * Builds the stacked-area series for the current page's rows from their
 * activityOverTime: the top rows get their own band, the tail folds into an
 * "Others (n)" band. Emissions and spend series share the same banding.
 *
 * The series is dense, and null means different things by tense: a completed
 * month nobody traded in keeps its point (an all-zero stack is the truth),
 * while months that haven't finished yet are dropped — stacked bands can't
 * express "no data yet" without reading as a collapse to zero. Within a kept
 * month, a row that did not trade counts as a zero contribution: these bands
 * are stacked, so breaking one mid-stack would misplace the bands above it.
 */
function buildStackedSeries(
  rows: readonly InsightsGroupByItem[],
  valueOf: (point: ActivitySeriesPoint) => number | null
) {
  const named = rows.slice(0, MAX_NAMED_SERIES);
  const tail = rows.slice(MAX_NAMED_SERIES);
  const othersName = tail.length ? `Others (${tail.length})` : null;

  const dates = [
    ...new Set(
      rows.flatMap((row) => row.activityOverTime.map((point) => point.date))
    ),
  ].sort();

  const valueFor = (row: InsightsGroupByItem, date: string) => {
    const match = row.activityOverTime.find((entry) => entry.date === date);
    return match ? valueOf(match) : null;
  };

  const chartData = dates.flatMap((date) => {
    const allNull = rows.every((row) => valueFor(row, date) == null);
    if (allNull && !isCompletedMonth(date)) return [];

    const point: Record<string, number | string> = {
      label: format(parseISO(date), 'MMM yy'),
    };
    named.forEach((row) => {
      point[row.name] = valueFor(row, date) ?? 0;
    });
    if (othersName) {
      point[othersName] = tail.reduce(
        (sum, row) => sum + (valueFor(row, date) ?? 0),
        0
      );
    }
    return [point];
  });

  const chartConfig: ChartConfig = {};
  named.forEach((row, index) => {
    chartConfig[row.name] = { label: row.name, color: SERIES_COLORS[index] };
  });
  if (othersName) {
    chartConfig[othersName] = { label: othersName, color: OTHERS_COLOR };
  }

  return { chartData, chartConfig };
}

type Props = {
  rows: readonly InsightsGroupByItem[];
  dimensionLabel: string;
  currency?: string | null;
  hasSpendData: boolean;
  /** Without the cost feature the spend charts stay listed but locked. */
  hasCostFeature: boolean;
  /** Row id of the table row being hovered — highlights its scatter point. */
  highlightedId?: string | null;
};

/**
 * Chart section for a grouped view, plotting the rows on the current table
 * page: emissions-vs-spend and intensity scatter comparisons, plus per-group
 * over-time stacked areas.
 */
export function InsightsGroupedCharts({
  rows,
  dimensionLabel,
  currency,
  hasSpendData,
  hasCostFeature,
  highlightedId,
}: Props) {
  const [rawChartType, setChartType] = useInsightsChartType();
  const { track } = useAnalytics();
  // A locked account never renders a spend chart, whatever the URL says —
  // the page also resets the param, but that lands a render later.
  const chartType =
    !hasCostFeature && SPEND_CHART_TYPES.includes(rawChartType)
      ? 'emissions'
      : rawChartType;
  const symbol = getCurrencySymbol(currency) || '£';

  // Points are rating-coloured only when the response provides ratings.
  const hasRatings = rows.some((row) => row.rating != null);

  const tooltipRows = (row: InsightsGroupByItem) => [
    ...(row.weightKg != null
      ? [{ label: 'Weight', value: `${formatCompact(row.weightKg)} kg` }]
      : []),
    ...(row.emissionsTonnes != null
      ? [{ label: 'Emissions', value: formatTonnes(row.emissionsTonnes) }]
      : []),
    ...(row.spend != null
      ? [
          {
            label: 'Spend',
            value: `${symbol}${formatCompact(Number(row.spend))}`,
          },
        ]
      : []),
  ];

  const scatterPoints: ScatterPoint[] = useMemo(
    () =>
      rows
        .filter((row) => row.spend != null)
        .map((row) => ({
          x: Number(row.spend),
          y: row.emissionsTonnes ?? 0,
          label: row.name,
          id: insightsRowId(row),
          tooltipRows: tooltipRows(row),
          color: ratingColor(row.rating),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, symbol]
  );

  const intensityPoints: ScatterPoint[] = useMemo(
    () =>
      rows
        .filter((row) => row.spendPerKg != null && row.emissionsPerKg != null)
        .map((row) => ({
          x: Number(row.spendPerKg),
          y: row.emissionsPerKg ?? 0,
          z: row.weightKg ?? 0,
          label: row.name,
          id: insightsRowId(row),
          tooltipRows: tooltipRows(row),
          color: ratingColor(row.rating),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, symbol]
  );

  const emissionsSeries = useMemo(
    () => buildStackedSeries(rows, (point) => point.emissionsTonnes),
    [rows]
  );
  const spendSeries = useMemo(
    () =>
      buildStackedSeries(rows, (point) =>
        point.spend != null ? Number(point.spend) : null
      ),
    [rows]
  );

  const isScatter = chartType === 'scatter' || chartType === 'intensity';
  const isSpendChart = chartType === 'spend' || isScatter;
  const showEmptyState = isSpendChart && !hasSpendData;
  const activeSeries = chartType === 'spend' ? spendSeries : emissionsSeries;

  const dimension = dimensionLabel.toLowerCase();
  const description = {
    emissions: `Explore emissions over time for each ${dimension}. Showing top results based on the table below.`,
    spend: `Explore spend over time for each ${dimension}. Showing top results based on the table below.`,
    scatter: `Compare your overall spend and emissions per ${dimension}. Showing top results based on the table below.`,
    intensity: `Compare carbon intensity and average price per ${dimension}, with bubble size indicating the overall weight purchased.`,
  }[chartType];
  // "Site emissions vs spend" — the section title carries the dimension; the
  // selector menu keeps the short labels.
  const sectionTitle = `${dimensionLabel} ${CHART_LABELS[chartType].toLowerCase()}`;

  return (
    <DashboardSection
      title={sectionTitle}
      description={description}
      actions={
        <SimpleSelect
          className="w-56"
          options={(Object.keys(CHART_LABELS) as InsightsChartType[]).map(
            (key) => {
              const locked = !hasCostFeature && SPEND_CHART_TYPES.includes(key);
              return {
                value: CHART_LABELS[key],
                disabled: locked,
                icon: locked ? (
                  <Icons.lock className="size-3.5 text-gray-400" />
                ) : undefined,
              };
            }
          )}
          value={CHART_LABELS[chartType]}
          onValueChange={(label) => {
            const next =
              (Object.keys(CHART_LABELS) as InsightsChartType[]).find(
                (key) => CHART_LABELS[key] === label
              ) ?? 'emissions';
            void setChartType(next);
            track('Switched PG Insights Chart Type', {
              chart_type: CHART_LABELS[next],
              explore_dimension: dimensionLabel,
            });
          }}
        />
      }
    >
      <Card className="flex flex-col gap-3 p-5">
        {showEmptyState ? (
          <InsightsNoSpendData />
        ) : isScatter ? (
          <div key={chartType} className="h-80 w-full">
            <ChartAxisTitles
              yTitle={
                chartType === 'scatter'
                  ? 'Emissions (tCO₂e)'
                  : 'Carbon intensity (kgCO₂e/kg)'
              }
              xTitle={
                chartType === 'scatter'
                  ? `Spend (${symbol})`
                  : `Average price (${symbol}/kg)`
              }
            >
              <SimpleScatterChart
                data={chartType === 'scatter' ? scatterPoints : intensityPoints}
                highlightedId={highlightedId}
                legend={
                  hasRatings ? (
                    <div className="flex items-center gap-3">
                      <Text variant="subtle" className="text-xs font-medium">
                        Carbon rating
                      </Text>
                      <div className="flex items-center gap-1.5">
                        {RATING_ORDER.map((rating) => (
                          <RatingCloud
                            key={rating}
                            rating={rating}
                            className="w-6"
                          />
                        ))}
                      </div>
                    </div>
                  ) : undefined
                }
                margin={{ top: 16, right: 24, bottom: 8, left: 0 }}
                xFormatter={(value) =>
                  chartType === 'scatter'
                    ? `${symbol}${formatCompact(value)}`
                    : `${symbol}${value.toFixed(2)}`
                }
                yFormatter={(value) =>
                  chartType === 'scatter'
                    ? formatCompact(value)
                    : value.toFixed(2)
                }
              />
            </ChartAxisTitles>
          </div>
        ) : (
          <div key={chartType} className="h-80 w-full">
            <ChartAxisTitles
              yTitle={
                chartType === 'spend'
                  ? `Spend (${symbol})`
                  : 'Emissions (tCO₂e)'
              }
            >
              <SimpleAreaChart
                chartConfig={activeSeries.chartConfig}
                chartData={activeSeries.chartData}
                className="aspect-auto size-full"
                nameKey="label"
                dataKey="label"
                stacked
                reverseTooltipItems
                strokeWidth={2}
                fillOpacity={0.7}
                margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
                YAxisProps={{ width: 52, tickMargin: 4 }}
                tooltipValueFormatter={(value) =>
                  chartType === 'spend'
                    ? `${symbol}${formatCompact(Number(value))}`
                    : formatTonnes(Number(value))
                }
              />
            </ChartAxisTitles>
          </div>
        )}
      </Card>
    </DashboardSection>
  );
}
